import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronRight as ChevronRightIcon } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { useBreakpointsContext } from "@air/provider-media-query";
import { memo, useCallback, useMemo } from "react";
import isEqual from "react-fast-compare";
import { GalleryItemType } from "~/components/Gallery/types";
import { useGetPublicBoardMenuOptionsCallback } from "~/components/PublicBoard/useGetPublicBoardMenuOptionsCallback";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { BoardTableRow } from "~/components/TableView/BoardTableRow";
import { MobileTableRowContainer } from "~/components/TableView/MobileTableRow/MobileTableRowContainer";
import { PublicBoardNameCell } from "~/components/TableView/PrivateTableView/PublicBoardNameCell";
import { PublicBoardCustomFieldTableCell } from "~/components/TableView/TableRow/CustomFieldTableCell/PublicBoardCustomFieldTableCell";
import { DesktopTableRowContainer } from "~/components/TableView/TableRow/DesktopTableRowContainer";
import { TABLE_ROW } from "~/constants/testIDs";
import { useOpenPublicBoardInNewTab } from "~/hooks/useOpenPublicBoardInNewTab";
import { usePublicSelectionMenuOptions } from "~/hooks/usePublicSelectionMenuOptions";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { useGoToPublicBoard } from "~/swr-hooks/boards/useGoToPublicBoard";
import { canChangeBoardCustomFields } from "~/utils/permissions/boardPermissions";
import { canGenerateZip } from "~/utils/permissions/taskPermissions";
export const PublicBoardTableRow = /*#__PURE__*/ memo((param)=>{
    let { data: board, style } = param;
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { permissions } = usePublicPermissions();
    const getItemMenuOptions = getBoardMenuOptionsCallback({
        item: board
    });
    const { goToPublicBoard } = useGoToPublicBoard();
    const { openPublicBoardInNewTab } = useOpenPublicBoardInNewTab();
    const onItemClick = useCallback((trackLocation)=>goToPublicBoard(board, trackLocation), [
        board,
        goToPublicBoard
    ]);
    const onItemCmdClick = useCallback(()=>openPublicBoardInNewTab({
            board
        }), [
        board,
        openPublicBoardInNewTab
    ]);
    const canDownload = canGenerateZip(permissions);
    const canEditCustomFields = canChangeBoardCustomFields(permissions);
    const isSelectable = canDownload || canEditCustomFields;
    const memoizedBoardProps = useMemo(()=>({
            isSelectable,
            onItemClick,
            onItemCmdClick,
            getSelectionMenuOptions,
            getItemMenuOptions
        }), [
        getItemMenuOptions,
        getSelectionMenuOptions,
        isSelectable,
        onItemClick,
        onItemCmdClick
    ]);
    const renderNameCell = useCallback((param)=>{
        let { isSelected, isHovering } = param;
        return /*#__PURE__*/ _jsx(PublicBoardNameCell, {
            isHovering: isHovering,
            isSelected: isSelected,
            board: board,
            ...memoizedBoardProps
        });
    }, [
        board,
        memoizedBoardProps
    ]);
    return isAboveMediumScreen ? /*#__PURE__*/ _jsx(DesktopTableRowContainer, {
        style: style,
        "data-testid": "".concat(TABLE_ROW, "-").concat(GalleryItemType.board),
        "data-title": board.title,
        children: /*#__PURE__*/ _jsx(BoardTableRow, {
            renderNameCell: renderNameCell,
            data: board,
            boardProps: memoizedBoardProps,
            CustomFieldTableCell: PublicBoardCustomFieldTableCell,
            canSelect: isSelectable
        })
    }) : /*#__PURE__*/ _jsxs(MobileTableRowContainer, {
        style: {
            paddingLeft: responsiveHorizontalPadding,
            paddingRight: responsiveHorizontalPadding,
            ...style
        },
        children: [
            renderNameCell({
                isSelected: false,
                isHovering: false
            }),
            /*#__PURE__*/ _jsx(IconButton, {
                appearance: "ghost",
                color: "grey",
                icon: ChevronRightIcon,
                onClick: ()=>{
                    memoizedBoardProps.onItemClick("table-cell");
                },
                size: "extra-large",
                className: "h-full",
                label: "View board"
            })
        ]
    });
}, isEqual);
PublicBoardTableRow.displayName = "PublicBoardTableRow";
